import { useState } from "react";
import type { ExerciseWithTarget, LastPerformance } from "./useSessionLogging";
import type { SessionSet } from "../../types/database";
import { ExerciseGif } from "../../components/ui/ExerciseGif";

interface Props {
  item: ExerciseWithTarget;
  last: LastPerformance | undefined;
  logged: SessionSet[] | undefined;
  onLog: (values: { weight_kg: number | null; reps: number | null; rir: number | null }) => void;
  onRemove: () => void;
}

export function ExerciseRow({ item, last, logged, onLog, onRemove }: Props) {
  const [editing, setEditing] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [weight, setWeight] = useState(String(last?.weight_kg ?? ""));
  const [reps, setReps] = useState(String(last?.reps ?? item.target_reps_min ?? ""));
  const [rir, setRir] = useState(String(last?.rir ?? item.target_rir ?? ""));

  const doneCount = logged?.length ?? 0;
  const targetSets = item.target_sets ?? 0;
  const repsLabel =
    item.target_reps_min && item.target_reps_max
      ? item.target_reps_min === item.target_reps_max
        ? `${item.target_reps_min} reps`
        : `${item.target_reps_min}-${item.target_reps_max} reps`
      : null;

  // Tap 1: registra la serie ya mismo con los últimos valores conocidos (o el objetivo si no hay historial).
  function quickLog() {
    onLog({
      weight_kg: last?.weight_kg ?? null,
      reps: last?.reps ?? item.target_reps_min ?? null,
      rir: last?.rir ?? item.target_rir ?? null,
    });
  }

  // Tap 2: confirma valores ajustados manualmente en el editor.
  function confirmEdit() {
    onLog({
      weight_kg: weight ? Number(weight) : null,
      reps: reps ? Number(reps) : null,
      rir: rir ? Number(rir) : null,
    });
    setEditing(false);
  }

  const hasMedia = item.exercise.image_urls?.length > 0;

  return (
    <div className="border-t border-line py-[11px] first:border-t-0">
      <div className="flex items-center justify-between gap-2">
        <button
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
          onClick={() => hasMedia && setShowInfo((v) => !v)}
        >
          {hasMedia && (
            <ExerciseGif
              urls={item.exercise.image_urls}
              alt={item.exercise.name}
              className="h-11 w-11 shrink-0 rounded-md"
            />
          )}
          <div className="min-w-0">
            <div className="truncate text-[14.5px] font-medium">{item.exercise.name}</div>
            <small className="mt-0.5 block truncate font-mono text-[9.5px] tracking-[0.08em] text-muted">
              {doneCount}/{targetSets || "—"} series
              {repsLabel && ` · objetivo ${repsLabel}`}
              {last?.weight_kg != null && ` · última: ${last.weight_kg} kg × ${last.reps ?? "—"}`}
            </small>
          </div>
        </button>
        <div className="flex shrink-0 items-center gap-2">
          <button
            onClick={() => setEditing((v) => !v)}
            aria-label="Ajustar valores"
            className="grid h-8 w-8 place-items-center rounded-full border border-line text-muted"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
          </button>
          <button
            onClick={quickLog}
            className="whitespace-nowrap rounded-full bg-accent-soft px-3 py-2 font-mono text-xs text-accent-bright"
          >
            + Serie {doneCount + 1}
          </button>
        </div>
      </div>

      {showInfo && item.exercise.instructions.length > 0 && (
        <ol className="ml-[52px] mt-2.5 list-decimal space-y-1 pl-4 font-body text-[12.5px] text-text/85">
          {item.exercise.instructions.map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
      )}

      {editing && (
        <div className="mt-2.5 flex items-end gap-2 rounded-inner border border-line bg-bg1 p-2.5">
          <label className="flex-1">
            <span className="mb-1 block font-mono text-[8.5px] uppercase tracking-[0.1em] text-muted">Kg</span>
            <input
              inputMode="decimal"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full rounded-md border border-line bg-transparent px-2 py-1.5 text-sm text-text outline-none focus:border-accent"
            />
          </label>
          <label className="flex-1">
            <span className="mb-1 block font-mono text-[8.5px] uppercase tracking-[0.1em] text-muted">Reps</span>
            <input
              inputMode="numeric"
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              className="w-full rounded-md border border-line bg-transparent px-2 py-1.5 text-sm text-text outline-none focus:border-accent"
            />
          </label>
          <label className="flex-1">
            <span className="mb-1 block font-mono text-[8.5px] uppercase tracking-[0.1em] text-muted">RIR</span>
            <input
              inputMode="decimal"
              value={rir}
              onChange={(e) => setRir(e.target.value)}
              className="w-full rounded-md border border-line bg-transparent px-2 py-1.5 text-sm text-text outline-none focus:border-accent"
            />
          </label>
          <button
            onClick={confirmEdit}
            className="rounded-md bg-accent px-3 py-1.5 font-mono text-xs text-white"
          >
            OK
          </button>
        </div>
      )}

      {editing && (
        <button
          onClick={onRemove}
          className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-red-400"
        >
          Quitar ejercicio del programa
        </button>
      )}
    </div>
  );
}
